import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { servicesData } from '../mock';
import { Sparkles, Droplets, Wind, Hammer, Building, Star, ChevronDown, Phone, Clock, Shield } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';

const iconMap = {
  Sparkles: <Sparkles size={32} />,
  Droplets: <Droplets size={32} />,
  Wind: <Wind size={32} />,
  Hammer: <Hammer size={32} />,
  Building: <Building size={32} />
};

export const Cleaning = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const data = servicesData.cleaning;

  const serviceTypes = [
    {
      title: 'Particuliers',
      description: 'Entretien régulier de votre domicile',
      features: ['Ménage hebdomadaire', 'Repassage', 'Grand nettoyage', 'Vitres']
    },
    {
      title: 'Entreprises',
      description: 'Nettoyage de vos locaux professionnels',
      features: ['Bureaux', 'Commerces', 'Copropriétés', 'Après travaux']
    }
  ];

  const testimonials = [
    {
      name: 'Claire D.',
      rating: 5,
      comment: 'Impeccable depuis 3 ans ! La même personne vient chaque semaine, c\'est rassurant et la qualité est toujours au rendez-vous.',
      service: 'Ménage hebdomadaire'
    },
    {
      name: 'CERIG Fontaine sur Saone',
      rating: 5,
      comment: 'Service professionnel pour le nettoyage de notre restaurant. Ponctualité et efficacité sont leurs maîtres mots.',
      service: 'Nettoyage commercial'
    }
  ];

  const faqs = [
    {
      question: 'Fournissez-vous le matériel de nettoyage ?',
      answer: 'Oui, nous fournissons tout le matériel et les produits professionnels nécessaires. Si vous préférez utiliser vos propres produits, c\'est également possible.'
    },
    {
      question: 'Puis-je modifier la fréquence de passage ?',
      answer: 'Oui, vous pouvez adapter la fréquence selon vos besoins : hebdomadaire, bihebdomadaire, mensuel ou ponctuel.'
    },
    {
      question: 'Que se passe-t-il en cas d\'absence de l\'intervenant ?',
      answer: 'Nous assurons un service de remplacement pour garantir la continuité du service.'
    },
    {
      question: 'Proposez-vous des services spécialisés ?',
      answer: 'Oui, nous proposons également le nettoyage de vitres, le détartrage, le nettoyage après travaux et le grand ménage de printemps.'
    }
  ];

  return (
    <div className="page-service">
      <section className="service-hero">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <h1 className="display-medium">{data.title}</h1>
            <p className="body-medium hero-subtitle">{data.description}</p>
          </ScrollReveal>
        </div>
      </section>

      <section className="service-image-section">
        <div className="container">
          <ScrollReveal animation="zoom-in">
            <div className="service-images-dual">
              <img 
                src="/images/repassage.jpg" 
                alt="Ménage professionnel" 
                className="service-dual-image"
              />
              <img 
                src="/images/cleaning-2.jpg" 
                alt="Nettoyage professionnel" 
                className="service-dual-image"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Service Types */}
      <section className="service-types-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Pour Qui ?</h2>
              <p className="body-medium">Des solutions adaptées à chaque besoin</p>
            </div>
          </ScrollReveal>
          <div className="service-types-grid">
            {serviceTypes.map((type, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 150}>
                <div className="service-type-card">
                  <h3 className="heading-2">{type.title}</h3>
                  <p className="body-medium">{type.description}</p>
                  <ul className="features-list">
                    {type.features.map((feature, i) => (
                      <li key={i}>
                        <Sparkles size={18} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="service-details">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Nos Prestations</h2>
            </div>
          </ScrollReveal>
          <div className="network-grid">
            {data.services.map((service, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="network-card service-detail-card">
                  <div className="feature-icon">{iconMap[service.icon]}</div>
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-medium">{service.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Guarantee */}
      <section className="quality-section">
        <div className="container">
          <div className="quality-content">
            <ScrollReveal animation="fade-up">
              <div className="quality-badges">
                <div className="quality-badge">
                  <Shield size={40} />
                  <h3 className="heading-2">Garantie Qualité</h3>
                  <p className="body-medium">Si vous n'êtes pas satisfait, nous revenons gratuitement</p>
                </div>
              <div className="quality-badge">
                <Clock size={40} />
                <h3 className="heading-2">Ponctualité</h3>
                <p className="body-medium">Respect strict des horaires convenues</p>
              </div>
            </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Témoignages</h2>
            </div>
          </ScrollReveal>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="testimonial-card">
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} fill="var(--brand-primary)" color="var(--brand-primary)" />
                    ))}
                  </div>
                  <p className="testimonial-comment">"{testimonial.comment}"</p>
                  <div className="testimonial-author">
                    <div className="author-info">
                      <strong>{testimonial.name}</strong>
                      <span>{testimonial.service}</span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Questions Fréquentes</h2>
            </div>
          </ScrollReveal>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 50}>
                <div className="faq-item">
                  <button 
                    className={`faq-question ${openFaq === index ? 'active' : ''}`}
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={24} className={`faq-icon ${openFaq === index ? 'rotated' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="faq-answer">
                      <p className="body-medium">{faq.answer}</p>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="cta-content">
              <h2 className="heading-1">Besoin d'un service de ménage ?</h2>
              <p className="body-medium">
                Profitez d'un intérieur impeccable sans effort
              </p>
              <div className="cta-buttons">
                <Link to="/contact#devis" className="btn-primary btn-cta">Demander un devis</Link>
                <a href="tel:0481119591" className="btn-secondary btn-cta">
                  <Phone size={20} />
                  04 81 11 95 91
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};